import { useEffect, useState, memo } from "react";
import { useRoomContext } from "@livekit/components-react";
import TextareaAutosize from "react-textarea-autosize";
import { useGlobalState } from "@/hooks/useGlobalState";
import { APP_NAME } from "@/configs/app";
import { upperFirst } from "@/utils/helpers";
import { getBrowserMicPermission } from "@/utils/get-browser-mic-permission";
import { setValue } from "@/utils/storage";
import { useAgentTimeout } from "@/providers/AgentTimeoutProvider";
import shoppingBasket from "/images/shoping-basket.svg";
import Union from "/images/Union.svg";
import { useShadowRoot } from "@/hooks/use-shadow-root";
import { captureError } from "@/utils/logger";

interface ChatFooterTextProps {
  onBasketClick?: () => void;
  agentState: string;
  cartItems: number;
  sendChat: (message: string) => void;
}

export const ChatFooterText = memo(function ChatFooterText({
  onBasketClick,
  agentState,
  cartItems,
  sendChat,
}: ChatFooterTextProps) {
  const shadowRoot = useShadowRoot();
  const room = useRoomContext();
  const { setChatMode, setMicPermission } = useGlobalState();
  const { handleUserAction } = useAgentTimeout();
  const [messageText, setMessageText] = useState("");

  const handleSendMessage = () => {
    if (messageText.length > 0) {
      sendChat(messageText);
      setMessageText("");
      setValue("last_interaction", Date.now().toString());
      handleUserAction();
    }
  };

  useEffect(() => {
    if (
      agentState !== "connecting" &&
      agentState !== "initializing" &&
      agentState !== "disconnected"
    ) {
      const audio = shadowRoot?.querySelectorAll(`audio`);

      if (audio && audio.length > 0) {
        (audio[0] as HTMLAudioElement).muted = true;
      }
    }
  }, [agentState]);

  const handleBlur = () => {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) {
      handleSendMessage();
    }
  };

  return (
    <div className="nut-text-chat-footer nut-w-full nut-flex nut-items-center nut-justify-center nut-gap-[10px] nut-bg-white nut-mt-3">
      <div className="nut-text-caht-input nut-w-full [@media(max-width:768px)]:nut-w-[calc(100%-50px)] nut-h-[68px] nut-rounded-[1998px] nut-border-2 nut-border-[#D8D8D8]">
        <div className="nut-h-full nut-px-5 nut-flex nut-items-center nut-gap-4 ">
          <TextareaAutosize
            maxRows={3}
            style={{
              height: 46,
              boxShadow: "none",
            }}
            value={messageText}
            onChange={(event) => setMessageText(event.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (messageText.length > 0 && e.key === "Enter") {
                handleSendMessage();
              }
            }}
            disabled={
              agentState === "connecting" ||
              // agentState === "initializing" ||
              agentState === "disconnected"
            }
            placeholder={
              agentState === "connecting" || agentState === "initializing"
                ? `${upperFirst(agentState)}...`
                : `Reply to ${APP_NAME}...`
            }
            onHeightChange={() => setMessageText(messageText.replace("\n", ""))}
            className="nut-flex-1 nut-min-w-0 nut-h-full nut-bg-transparent focus:nut-outline-none nut-text-[16px] nut-leading-[20.63px] nut-tracking-[0%] nut-text-black nut-opacity-60"
          />

          <div className="nut-flex nut-items-center nut-gap-[16px]">
            <button
              onClick={async () => {
                // This click is the browser-approved user gesture needed to
                // resume remote audio in Chrome, Safari, and embedded views.
                try {
                  await room.startAudio();
                } catch (error) {
                  captureError("Failed to start LiveKit audio playback", error);
                }

                setChatMode("voice");

                setMicPermission(await getBrowserMicPermission());
              }}
            >
              <img
                src={Union}
                title="Switch to voice chat"
                className="nut-w-[24.7px] nut-h-[28.5px]"
              />
            </button>
          </div>
        </div>
      </div>
      <button
        onClick={onBasketClick}
        className="nut-flex-shrink-0 nut-w-[68px] nut-h-[68px] nut-flex nut-items-center nut-justify-center nut-rounded-full nut-border-2 nut-border-[#D8D8D8] nut-relative"
      >
        {cartItems > 0 && (
          <div className="nut-absolute -nut-top-1 -nut-right-1 nut-bg-magic-gradient nut-text-white nut-rounded-full nut-w-6 nut-h-6 nut-flex nut-items-center nut-justify-center nut-text-sm">
            {cartItems}
          </div>
        )}
        <img
          src={shoppingBasket}
          title="Open basket"
          alt="comment-icon"
          className="nut-w-[23.3px] nut-h-[22.17px]"
        />
      </button>
    </div>
  );
});
